/**
 * Exportar e importar una rutina como archivo (Req. 27).
 *
 * Exportar solo lee. Importar escribe en una sola transacción y nada más que
 * eso: los ejercicios que falten y la rutina con sus ejercicios (Req. 27.3).
 * Si algo falla, no queda nada a medias.
 */

import { db } from './schema';
import { nuevoId } from './ids';
import { enEscritura } from './tx';
import type { Exercise, Routine, RoutineExercise } from './types';
import { ahoraIso } from '@/domain/dates';
import { ErrorValidacion } from '@/domain/errors';
import {
  analizarArchivoDeRutina,
  armarArchivoDeRutina,
  nombreDeArchivoDeRutina,
  nombreLibreDeRutina,
  planDeImportacion,
  serializarArchivoDeRutina,
  type ArchivoRutina,
  type PlanDeImportacion,
} from '@/domain/rutina-archivo';

export interface RutinaExportada {
  /** El nombre del archivo: "rutina-lunes.rutina.json". */
  nombre: string;
  texto: string;
  /** Cuántos ejercicios lleva, para decirlo antes de entregarlo. */
  ejercicios: number;
  /** Los que no se pudieron incluir porque su ejercicio ya no existe. */
  sinEjercicio: number;
}

/**
 * LECTURA PURA: el archivo de una rutina, listo para entregarlo (Req. 27.1).
 * No lleva cargas ni historial: una rutina es el plan.
 */
export async function exportarRutina(routineId: string, ahora: string = ahoraIso()): Promise<RutinaExportada> {
  const rutina = await db.routines.get(routineId);
  if (rutina === undefined) throw new ErrorValidacion('Esa rutina ya no existe.');
  const items = (await db.routineExercises.where('routineId').equals(routineId).toArray()).sort(
    (a, b) => a.orden - b.orden,
  );
  const ejercicios = await db.exercises.bulkGet(items.map((i) => i.exerciseId));
  const archivo = armarArchivoDeRutina(
    rutina,
    items.map((item, i) => ({ item, ejercicio: ejercicios[i] })),
    ahora,
  );
  if (archivo.rutina.ejercicios.length === 0) {
    throw new ErrorValidacion('Esa rutina no tiene ejercicios que compartir.');
  }
  return {
    nombre: nombreDeArchivoDeRutina(rutina.nombre),
    texto: serializarArchivoDeRutina(archivo),
    ejercicios: archivo.rutina.ejercicios.length,
    sinEjercicio: items.length - archivo.rutina.ejercicios.length,
  };
}

export type RutinaParaImportar =
  | { ok: true; archivo: ArchivoRutina; plan: PlanDeImportacion }
  | { ok: false; error: string };

/**
 * LECTURA PURA: qué traería ese archivo, para mostrarlo antes de guardar
 * (Req. 27.2). El nombre se propone libre: si ya tienes una rutina con ese
 * nombre, se ofrece "(2)".
 */
export async function revisarRutinaDeArchivo(texto: string): Promise<RutinaParaImportar> {
  const leido = analizarArchivoDeRutina(texto);
  if (!leido.ok) return leido;
  const [ejercicios, rutinas] = await Promise.all([db.exercises.toArray(), db.routines.toArray()]);
  const nombre = nombreLibreDeRutina(
    leido.archivo.rutina.nombre,
    rutinas.map((r) => r.nombre),
  );
  return { ok: true, archivo: leido.archivo, plan: planDeImportacion(leido.archivo, ejercicios, nombre) };
}

/**
 * Guarda la rutina del archivo con el nombre indicado y crea los ejercicios
 * que falten, todo en una transacción (Req. 27.2, 27.3). No toca ninguna otra
 * rutina, ejercicio ni entrenamiento. Devuelve el id de la rutina nueva.
 * ESCRIBE.
 */
export async function importarRutina(
  archivo: ArchivoRutina,
  nombre: string,
  id: string = nuevoId('rut'),
  ahora: string = ahoraIso(),
): Promise<string> {
  const limpio = nombre.trim().replace(/\s+/g, ' ');
  if (limpio === '') throw new ErrorValidacion('Escribe el nombre de la rutina.', 'nombre');
  if (limpio.length > 60) throw new ErrorValidacion('El nombre es demasiado largo.', 'nombre');

  return enEscritura([db.routines, db.routineExercises, db.exercises], async () => {
    // Dos toques no crean dos rutinas
    if ((await db.routines.get(id)) !== undefined) return id;
    const delTelefono = await db.exercises.toArray();
    const plan = planDeImportacion(archivo, delTelefono, limpio);

    // Los que faltan se crean como propios, con la unidad y el incremento de
    // este teléfono: eso es de quien entrena, no de la rutina
    const ids: string[] = [];
    for (const v of plan.ejercicios) {
      if (v.exerciseId !== null) {
        ids.push(v.exerciseId);
        continue;
      }
      const nuevo: Exercise = {
        id: nuevoId('ej'),
        nombre: v.del.nombre,
        grupoMuscular: v.del.grupoMuscular,
        equipo: v.del.equipo,
        tipo: v.del.tipo,
        esBase: false,
        unidadCaptura: null,
        incrementoKg: null,
        notas: null,
        archivado: false,
        creadoEn: ahora,
        actualizadoEn: ahora,
      };
      await db.exercises.add(nuevo);
      ids.push(nuevo.id);
    }

    const orden = (await db.routines.toArray()).reduce((max, r) => Math.max(max, r.orden), -1) + 1;
    const rutina: Routine = {
      id,
      nombre: limpio,
      notas: archivo.rutina.notas,
      orden,
      ...(archivo.rutina.dias === undefined ? {} : { dias: [...archivo.rutina.dias] }),
      // Importada: no ocupa el lugar de una precargada
      plantilla: null,
      modalidad: archivo.rutina.modalidad ?? 'gimnasio',
      creadoEn: ahora,
      actualizadoEn: ahora,
    };
    await db.routines.add(rutina);

    const items: RoutineExercise[] = plan.ejercicios.map((v, i) => ({
      id: nuevoId('rje'),
      routineId: id,
      exerciseId: ids[i] as string,
      orden: i,
      series: v.del.series,
      repsMin: v.del.repsMin,
      repsMax: v.del.repsMax,
      descansoSeg: v.del.descansoSeg,
      notas: v.del.notas,
      biserie: v.del.biserie ?? null,
      descansoBiserieSeg: v.del.descansoBiserieSeg ?? null,
      ...(v.del.pausaSeg == null ? {} : { pausaSeg: v.del.pausaSeg }),
      ...(v.del.variante == null ? {} : { variante: v.del.variante }),
      ...(v.del.porLado === true ? { porLado: true } : {}),
    }));
    await db.routineExercises.bulkAdd(items);
    return id;
  });
}
